import { forwardRef, useCallback, useEffect, useRef, type TextareaHTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Visual size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Error message — triggers error styling when truthy */
  error?: string;
  /** Automatically grow the textarea to fit its content */
  autoResize?: boolean;
}

const SIZE_MAP = {
  sm: 'text-2xs px-2.5 py-1.5',
  md: 'text-sm px-3 py-2',
  lg: 'text-sm px-3.5 py-2.5',
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, size = 'md', error, autoResize = false, rows = 3, onChange, ...props }, ref) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        internalRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      },
      [ref],
    );

    const adjustHeight = useCallback(() => {
      const el = internalRef.current;
      if (!el || !autoResize) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }, [autoResize]);

    useEffect(() => {
      adjustHeight();
    }, [adjustHeight]);

    return (
      <div className="w-full">
        <textarea
          ref={setRefs}
          rows={autoResize ? 1 : rows}
          aria-invalid={error ? true : undefined}
          onChange={(e) => {
            onChange?.(e);
            adjustHeight();
          }}
          className={clsx(
            'w-full rounded-lg border bg-surface-1 text-ink placeholder:text-ink-dim/50',
            'transition-colors duration-150 resize-none',
            'focus:outline-hidden focus:border-primary focus:ring-2 focus:ring-primary/20',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border-danger focus:border-danger focus:ring-danger/20',
            !error && 'border-border',
            autoResize && 'overflow-hidden',
            SIZE_MAP[size],
            className,
          )}
          {...props}
        />
        {error && <p className="mt-1 text-2xs text-danger">{error}</p>}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
